// cart-crossell.js - Versiune cu localStorage cache
(function() {
  'use strict';
  
  let isLoading = false;
  let loaded = false;
  let slickInitialized = false;
  let cachedProducts = null;
  let lastCartHash = null;
  
  // Calculează un hash simplu al coșului
  function getCartHash(cart) {
    if (!cart.items || cart.items.length === 0) return null;
    return cart.items.map(item => `${item.product_id}:${item.quantity}`).join('|');
  }
  
  // Salvează în localStorage
  function cacheProducts(products, cartHash) {
    if (!products || products.length === 0) return;
    try {
      localStorage.setItem('crossell_products', JSON.stringify(products));
      localStorage.setItem('crossell_cart_hash', cartHash);
      localStorage.setItem('crossell_timestamp', Date.now().toString());
    } catch(e) { console.warn('localStorage error:', e); }
  }
  
  // Încarcă din localStorage
  function loadFromCache(cartHash) {
    try {
      const cachedHash = localStorage.getItem('crossell_cart_hash');
      const cached = localStorage.getItem('crossell_products');
      const timestamp = parseInt(localStorage.getItem('crossell_timestamp') || '0');
      const isExpired = (Date.now() - timestamp) > 30 * 60 * 1000; // 30 minute expiry
      
      if (!isExpired && cachedHash === cartHash && cached) {
        return JSON.parse(cached);
      }
    } catch(e) { console.warn('localStorage read error:', e); }
    return null;
  }
  
  function waitForCartDrawer() {
    return new Promise((resolve) => {
      if (customElements.get('cart-drawer')) {
        resolve();
        return;
      }
      const observer = new MutationObserver(() => {
        if (customElements.get('cart-drawer')) {
          observer.disconnect();
          resolve();
        }
      });
      observer.observe(document.head, { childList: true, subtree: true });
    });
  }
  
  async function init() {
    await waitForCartDrawer();
    
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    
    // Observă deschiderea drawer-ului
    const drawerObserver = new MutationObserver(() => {
      if (drawer.classList && drawer.classList.contains('active')) {
        ensureCrossellLoaded();
      }
    });
    drawerObserver.observe(drawer, { attributes: true, attributeFilter: ['class'] });
    
    // Observă când drawer-ul este re-renderat (după change quantity)
    const bodyObserver = new MutationObserver(() => {
      if (drawer.classList && drawer.classList.contains('active')) {
        // Dacă există containerul dar e gol, re-populează din cache
        const track = document.getElementById('crossell-track');
        if (track && cachedProducts && (track.innerHTML.includes('Se încarcă') || track.children.length === 0)) {
          renderProducts(cachedProducts, track);
        }
      }
    });
    bodyObserver.observe(document.body, { childList: true, subtree: true });
    
    if (drawer.classList && drawer.classList.contains('active')) {
      ensureCrossellLoaded();
    }
  }
  
  async function ensureCrossellLoaded() {
    if (loaded && cachedProducts) {
      // Deja avem produse, doar asigură-te că sunt în DOM
      const track = document.getElementById('crossell-track');
      if (track && (track.innerHTML.includes('Se încarcă') || track.children.length === 0)) {
        renderProducts(cachedProducts, track);
      }
      return;
    }
    if (!isLoading) {
      loadCrossell();
    }
  }
  
  async function loadCrossell() {
    if (isLoading) return;
    isLoading = true;
    
    const container = document.getElementById('cart-crossell-root');
    if (!container) {
      isLoading = false;
      return;
    }
    
    const track = document.getElementById('crossell-track');
    if (!track) {
      isLoading = false;
      return;
    }
    
    track.innerHTML = '<div class="crossell-loading">Se caută recomandări...</div>';
    
    try {
      const cartRes = await fetch('/cart.js');
      const cart = await cartRes.json();
      
      if (!cart.items || cart.items.length === 0) {
        track.innerHTML = '<div class="crossell-loading">Adaugă produse pentru recomandări</div>';
        isLoading = false;
        return;
      }
      
      const currentCartHash = getCartHash(cart);
      
      // Încearcă să încarce din cache
      const cached = loadFromCache(currentCartHash);
      if (cached && cached.length > 0) {
        console.log('Using cached crossell products');
        cachedProducts = cached;
        renderProducts(cached, track);
        loaded = true;
        isLoading = false;
        return;
      }
      
      const productIdsInCart = cart.items.map(item => item.product_id);
      const allRecommendations = [];
      
      const recommendationPromises = cart.items.map(async (item) => {
        try {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 3000);
          
          const recRes = await fetch(
            `/recommendations/products.json?product_id=${item.product_id}&limit=8&intent=related`,
            { signal: controller.signal }
          );
          clearTimeout(timeoutId);
          
          if (!recRes.ok) return [];
          
          const recData = await recRes.json();
          if (recData.products && recData.products.length > 0) {
            return recData.products.map(product => ({
              product: product,
              score: 1,
              sourceProductId: item.product_id
            }));
          }
          return [];
        } catch (err) {
          return [];
        }
      });
      
      const allResults = await Promise.all(recommendationPromises);
      for (const results of allResults) {
        allRecommendations.push(...results);
      }
      
      let finalProducts = [];
      
      if (allRecommendations.length > 0) {
        const uniqueProducts = new Map();
        for (const rec of allRecommendations) {
          const productId = rec.product.id;
          if (uniqueProducts.has(productId)) {
            uniqueProducts.get(productId).score += 1;
          } else {
            uniqueProducts.set(productId, { product: rec.product, score: rec.score });
          }
        }
        
        finalProducts = Array.from(uniqueProducts.values())
          .filter(item => !productIdsInCart.includes(item.product.id))
          .sort((a, b) => b.score - a.score)
          .map(item => item.product)
          .slice(0, 6);
      }
      
      if (finalProducts.length === 0) {
        const fallbackRes = await fetch('/collections/all/products.json?sort_by=created-descending&limit=8');
        const fallbackData = await fallbackRes.json();
        if (fallbackData.products) {
          finalProducts = fallbackData.products
            .filter(p => !productIdsInCart.includes(p.id))
            .slice(0, 6);
        }
      }
      
      if (finalProducts.length === 0) {
        track.innerHTML = '<div class="crossell-loading">Nu există recomandări disponibile</div>';
        isLoading = false;
        return;
      }
      
      // Salvează în cache
      cachedProducts = finalProducts;
      cacheProducts(finalProducts, currentCartHash);
      
      renderProducts(finalProducts, track);
      loaded = true;
      
    } catch (err) {
      console.error('Crossell error:', err);
      track.innerHTML = '<div class="crossell-loading">Eroare încărcare recomandări</div>';
    } finally {
      isLoading = false;
    }
  }
  
function renderProducts(products, track) {
  if (!products || products.length === 0) return;
  
  const html = products.map(product => {
    const variant = product.variants && product.variants[0] ? product.variants[0] : null;
    if (!variant) return '';
    
    const price = (variant.price / 100).toFixed(2);
    const imageUrl = product.featured_image || (product.images && product.images[0]) || '';
    const title = product.title.length > 40 ? product.title.substring(0, 37) + '...' : product.title;
    
    return `
      <div class="crossell-item" data-variant-id="${variant.id}">
        <div class="crossell-image-wrapper">
          <img src="${imageUrl}" alt="${escapeHtml(product.title)}" loading="lazy" onerror="this.src='data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'169\' height=\'169\' viewBox=\'0 0 169 169\'%3E%3Crect width=\'169\' height=\'169\' fill=\'%23f0f0f0\'/%3E%3Ctext x=\'84.5\' y=\'84.5\' text-anchor=\'middle\' dy=\'.3em\' fill=\'%23999\' font-size=\'14\'%3ENo image%3C/text%3E%3C/svg%3E'">
        </div>
        <div class="container_info_cs">
          <div class="container_info_cs_1">
            <div class="crossell-item-title">${escapeHtml(title)}</div>
            <div class="crossell-price">${price} lei</div>
          </div>
          <button class="crossell-add">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 22C9.55228 22 10 21.5523 10 21C10 20.4477 9.55228 20 9 20C8.44772 20 8 20.4477 8 21C8 21.5523 8.44772 22 9 22Z" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M20 22C20.5523 22 21 21.5523 21 21C21 20.4477 20.5523 20 20 20C19.4477 20 19 20.4477 19 21C19 21.5523 19.4477 22 20 22Z" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M1 1H5L7.68 14.39C7.77144 14.8504 8.02191 15.264 8.38755 15.5583C8.75318 15.8526 9.2107 16.009 9.68 16H19.4C19.8693 16.009 20.3268 15.8526 20.6925 15.5583C21.0581 15.264 21.3086 14.8504 21.4 14.39L23 6H6" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>



      </div>
    `;
  }).join('');
  
  track.innerHTML = html;
  
  // Atașează evenimente la butoane
  track.querySelectorAll('.crossell-add').forEach(btn => {
    btn.removeEventListener('click', handleAddToCart);
    btn.addEventListener('click', handleAddToCart);
  });
  
  // Reinițializează Slick Slider (distruge instanța veche dacă există)
  if (typeof $ !== 'undefined' && $.fn.slick) {
    // Distruge instanța Slick existentă
    if ($(track).hasClass('slick-initialized')) {
      $(track).slick('unslick');
    }
    // Inițializează din nou
    $(track).slick({
      slidesToShow: 2.15,
      slidesToScroll: 1,
      infinite: false,
      arrows: false,
      dots: false,
      responsive: [
        { breakpoint: 768, settings: { slidesToShow: 2, slidesToScroll: 1 } }
      ]
    });
  } else {
    console.warn('Slick Slider not loaded');
  }
}
  
  async function handleAddToCart(e) {
    e.preventDefault();
    e.stopPropagation();
    
    const btn = e.currentTarget;
    const item = btn.closest('.crossell-item');
    const variantId = item?.dataset.variantId;
    
    if (!variantId) return;
    
    const originalSvg = btn.innerHTML;
    btn.disabled = true;
    btn.innerHTML = '...';
    
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: parseInt(variantId), quantity: 1 })
      });
      
      if (res.ok) {
        btn.innerHTML = '✓';
        setTimeout(() => location.reload(), 500);
      } else {
        throw new Error();
      }
    } catch (err) {
      btn.innerHTML = 'Eroare';
      setTimeout(() => {
        btn.innerHTML = originalSvg;
        btn.disabled = false;
      }, 2000);
    }
  }
  
  function escapeHtml(str) {
    if (!str) return '';
    return str.replace(/[&<>]/g, (m) => {
      if (m === '&') return '&amp;';
      if (m === '<') return '&lt;';
      if (m === '>') return '&gt;';
      return m;
    });
  }
  
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();